import type { Entity, JsonLdResponse } from "@/react/story/types";

class ApiUtilsWrapper {

    getCollection<T extends Partial<Entity<unknown>>>(url: string, params: Record<string, unknown> = {}): Promise<JsonLdResponse<T>> {
        return this.request<JsonLdResponse<T>>("GET", this.buildUrl(url, params));
    }

    get<T extends Partial<Entity<unknown>>>(url: string): Promise<T> {
        return this.request<T>("GET", url);
    }

    save<T extends Partial<Entity<unknown>>>(url: string, o: Partial<T>): Promise<T> {
        if (o["@id"]) {
            return this.put(o);
        }

        return this.post(url, o);
    }

    post<T extends Partial<Entity<unknown>>>(url: string, o: Partial<T>): Promise<T> {
        return this.request<T>("POST", url, o);
    }

    put<T extends Partial<Entity<unknown>>>(o: Partial<T>): Promise<T> {
        return this.request<T>("PUT", o?.["@id"] ?? "", o);
    }

    delete(o: Entity<unknown>): Promise<undefined> {
        return this.request<undefined>("DELETE", o["@id"]);
    }

    private async request<T>(
        method: string,
        url: string,
        body: Record<string, unknown> | undefined = undefined
    ): Promise<T> {
        const response = await fetch(url, {
            method,
            body: body ? JSON.stringify(body) : undefined,
            headers: {
                "Content-Type": "application/ld+json",
                "Accept": "application/ld+json",
            },
        });

        if (!response.ok) {
            let detail = response.statusText;
            try {
                const json = await response.json();
                detail = json["description"] ?? json["detail"] ?? json["message"] ?? detail;
            } catch {
                // response body is not JSON, keep statusText
            }
            throw new Error(detail);
        }

        // DELETE => 204 No Content
        if (204 === response.status) {
            return undefined as T;
        }

        return await response.json();
    }

    private buildUrl(baseUrl: string, filters: Record<string, unknown>): string {
        const isAbsolute = /^https?:\/\//i.test(baseUrl);
        const url = new URL(baseUrl, isAbsolute ? undefined : window.location.origin);

        const append = (key: string, value: unknown) => {
            if (value === null || value === undefined) {
                return;
            }

            if (Array.isArray(value)) {
                value.forEach(v => append(key, v));

                return;
            }

            if (typeof value === "object") {
                Object.entries(value).forEach(([ k, v ]) => append(`${key}[${k}]`, v));

                return;
            }

            url.searchParams.append(key, String(value));
        };

        Object.entries(filters).forEach(([ key, value ]) => append(key, value));

        return url.toString();
    }

}

const ApiUtils = new ApiUtilsWrapper();

export default ApiUtils;
